/* eslint-disable unicorn/filename-case */
import Loading, { LoadingBall } from '@ingka/loading';
import Text from '@ingka/text';
import {
  useGetLotteryQuery,
  useGetLotteryTranslationsQuery,
} from '@lotta/self-service/lottery-fe/data-access-state';
import type { LotteryConfig, TranslationConfig } from '@lotta/shared/types-lottery-schemas';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export type WithLoadingLotteryProps = {
  retailUnit: string;
  lotteryId: string;
};

/**
 * HOC to help load lottery- and translation-config
 *
 * Puts them in the 'values'-prop like so:
 * `<Component /.../ values={{ lottery: ..., translation: ... }}` />
 *
 * Requires retailUnit and lotteryId as props.
 */
export const withLoadingValues = <P extends object>(
  Component: React.ComponentType<
    P & { values: { lottery: LotteryConfig; translation: TranslationConfig } }
  >
): React.FC<P & WithLoadingLotteryProps> => {
  return (props) => {
    const { retailUnit, lotteryId } = props;
    const { t } = useTranslation();
    const lotteryQuery = useGetLotteryQuery({
      retailUnit,
      lotteryId,
    });
    const translationsQuery = useGetLotteryTranslationsQuery({
      retailUnit,
      lotteryId,
    });

    const values = useMemo(() => {
      if (!lotteryQuery.data || !translationsQuery.data) {
        return undefined;
      }
      return {
        lottery: lotteryQuery.data.lottery,
        translation: translationsQuery.data.translations,
      };
    }, [lotteryQuery.data, translationsQuery.data]);

    if (lotteryQuery.isLoading || translationsQuery.isLoading) {
      return (
        <Loading text={`${t('general-fetching-lottery')}`}>
          <LoadingBall />
        </Loading>
      );
    }

    if (lotteryQuery.error || translationsQuery.error || !values) {
      return <Text headingSize="m">{t('general-something-went-wrong')}</Text>;
    }

    return <Component {...props} values={values} />;
  };
};
